/**
 * Generic "HH:MM" helpers, shared by every widget that reads a clock face.
 *
 * Sleep-log reads them as wall-clock times and workday-log reads them as
 * durations; neither meaning lives here. Anything that depends on which one
 * you meant — lateness windows, midnight wrapping, rejecting a zero-length
 * day — belongs to the widget, not to this file.
 */

const MINUTES_PER_DAY = 24 * 60;

const CLOCK_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isClockTime(value: unknown): value is string {
  return typeof value === "string" && CLOCK_TIME_PATTERN.test(value);
}

/** Minutes since midnight, or null when the value is not an "HH:MM" time. */
export function minutesSinceMidnight(value: unknown): number | null {
  if (!isClockTime(value)) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Parse what the user typed into a canonical "HH:MM", or null when it is
 * not a usable time yet.
 *
 * The app renders its own 24-hour field rather than `<input type="time">`,
 * which formats itself from the browser's UI language — not from the page,
 * not from the element's `lang`, and not from `LC_TIME` (docs/adr/0008). An
 * English-UI browser therefore shows AM/PM no matter what the markup says,
 * so the field is hand-built and this parser is what makes it forgiving:
 * "0016", "00:16", "7:5" and "23" all land somewhere sensible.
 */
export function parseTimeInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;

  let hours: string;
  let minutes: string;
  if (trimmed.includes(":")) {
    const parts = trimmed.split(":");
    if (parts.length !== 2) return null;
    [hours, minutes] = parts;
  } else {
    if (!/^\d+$/.test(trimmed)) return null;
    if (trimmed.length <= 2) {
      // A bare hour: "23" means 23:00, which is what someone typing a round
      // hour expects — and it still converges correctly mid-typing, since
      // "0" → 00:00 is superseded keystroke by keystroke up to "0016".
      hours = trimmed;
      minutes = "0";
    } else if (trimmed.length === 3) {
      hours = trimmed.slice(0, 1);
      minutes = trimmed.slice(1);
    } else if (trimmed.length === 4) {
      hours = trimmed.slice(0, 2);
      minutes = trimmed.slice(2);
    } else {
      return null;
    }
  }

  if (!/^\d{1,2}$/.test(hours) || !/^\d{1,2}$/.test(minutes)) return null;
  const h = Number(hours);
  const m = Number(minutes);
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "6t 29m", or "45m" under the hour. */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours === 0 ? `${rest}m` : `${hours}t ${rest}m`;
}

export { MINUTES_PER_DAY };
